import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function fmt(n) {
  // jsPDF's built-in helvetica doesn't render the NARROW NO-BREAK SPACE (U+202F)
  // used by fr-FR Intl as thousands separator → it appears as "/". Replace by a
  // regular space.
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" })
    .format(Number(n || 0))
    .replace(/\u202F|\u00A0/g, " ");
}

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("fr-FR"); } catch { return s; }
}

const COLOR_KLEIN = [0, 47, 167];
const COLOR_DARK = [15, 23, 42];
const COLOR_LIGHT = [243, 244, 246];

export function generateInvoicePDF(vente, paiements, settings) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const s = settings || {};
  const companyName = s.nom || "Gestion Concession Automobile";

  // ===== Header band (klein blue) =====
  doc.setFillColor(...COLOR_KLEIN);
  doc.rect(0, 0, pageWidth, 36, "F");

  // Logo (if provided as base64 data URL)
  let logoOffset = 14;
  if (s.logo_data_url) {
    try {
      const mime = s.logo_data_url.match(/data:image\/(png|jpeg|jpg|webp);/);
      if (mime) {
        doc.addImage(s.logo_data_url, mime[1].toUpperCase(), 14, 6, 24, 24);
        logoOffset = 44;
      }
    } catch (e) { /* ignore bad logo */ }
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text(companyName.toUpperCase(), logoOffset, 16);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  const tagline = [s.telephone, s.email].filter(Boolean).join("  ·  ");
  if (tagline) doc.text(tagline, logoOffset, 22);
  if (s.adresse) doc.text(s.adresse.substring(0, 80), logoOffset, 27);
  if (s.matricule_fiscal) doc.text(`SIRET : ${s.matricule_fiscal}`, logoOffset, 32);

  // REÇU label right
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("REÇU DE PAIEMENT", pageWidth - 14, 16, { align: "right" });
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(`N° ${(vente.id || "").slice(-8).toUpperCase()}`, pageWidth - 14, 22, { align: "right" });
  doc.text(`Date : ${fmtDate(vente.date_vente)}`, pageWidth - 14, 27, { align: "right" });

  doc.setTextColor(...COLOR_DARK);

  // ===== Client + Vehicle blocks =====
  const blockY = 48;
  // Client
  doc.setFillColor(...COLOR_LIGHT);
  doc.rect(14, blockY, (pageWidth - 28) / 2 - 3, 34, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...COLOR_KLEIN);
  doc.text("PAYÉ PAR", 18, blockY + 6);
  doc.setTextColor(...COLOR_DARK);
  doc.setFontSize(11);
  doc.text(vente.client_nom || "—", 18, blockY + 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  if (vente.client_telephone) doc.text(`Tél. : ${vente.client_telephone}`, 18, blockY + 21);

  // Vehicle
  const colX = (pageWidth - 28) / 2 + 17;
  doc.setFillColor(...COLOR_LIGHT);
  doc.rect(colX, blockY, (pageWidth - 28) / 2 - 3, 34, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...COLOR_KLEIN);
  doc.text("VÉHICULE", colX + 4, blockY + 6);
  doc.setTextColor(...COLOR_DARK);
  doc.setFontSize(11);
  doc.text(`${vente.marque || ""} ${vente.modele || ""}`, colX + 4, blockY + 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Plaque : ${vente.plaque || "—"}`, colX + 4, blockY + 21);
  if (vente.annee) doc.text(`Année : ${vente.annee}`, colX + 4, blockY + 27);
  if (vente.vin) doc.text(`VIN : ${vente.vin}`, colX + 4, blockY + 32);

  // ===== Items table (prices HT) =====
  const body = [[
    `Véhicule ${vente.marque || ""} ${vente.modele || ""} ${vente.annee ? `(${vente.annee})` : ""} - Plaque ${vente.plaque || "—"}`,
    fmt(vente.prix_vente),
  ]];
  if (vente.cout_interventions > 0) {
    body.push(["Prestations mécaniques (atelier)", fmt(vente.cout_interventions)]);
  }
  autoTable(doc, {
    startY: blockY + 42,
    head: [["Désignation", "Montant HT"]],
    body,
    headStyles: { fillColor: COLOR_KLEIN, textColor: 255, fontStyle: "bold", fontSize: 10 },
    columnStyles: { 1: { halign: "right", cellWidth: 50 } },
    styles: { fontSize: 10, cellPadding: 4 },
    theme: "striped",
    margin: { left: 14, right: 14 },
  });

  // ===== Totals box (right side) — better organised =====
  const tY = (doc.lastAutoTable?.finalY || 100) + 8;
  const prixVehicule = Number(vente.prix_vente || 0);
  const prixInterventions = Number(vente.cout_interventions || 0);
  const total = vente.prix_total != null ? Number(vente.prix_total) : prixVehicule + prixInterventions;
  const encaisse = Number(vente.montant_paye || 0);
  const restant = vente.montant_restant != null ? Number(vente.montant_restant) : total - encaisse;

  // Compact box on the right
  const boxX = pageWidth - 96;
  const boxW = 82;
  const lineH = 7;
  // Determine the rows we'll show (sub-total only if interventions exist)
  const hasInterv = prixInterventions > 0;
  const rows = [];
  if (hasInterv) {
    rows.push({ label: "Véhicule HT", value: fmt(prixVehicule) });
    rows.push({ label: "Prestations HT", value: fmt(prixInterventions) });
  }
  rows.push({ label: "Total HT", value: fmt(total), bold: true });
  rows.push({ label: "Encaissé", value: fmt(encaisse) });
  rows.push({
    label: "Restant dû",
    value: fmt(restant),
    bold: true,
    color: restant > 0 ? [220, 38, 38] : [16, 185, 129],
    separator: true,
  });

  const boxH = rows.length * lineH + 6;
  doc.setFillColor(...COLOR_LIGHT);
  doc.rect(boxX, tY, boxW, boxH, "F");

  let rowY = tY + 6;
  rows.forEach((r) => {
    if (r.separator) {
      doc.setDrawColor(180);
      doc.line(boxX + 4, rowY - 3, boxX + boxW - 4, rowY - 3);
    }
    if (r.color) doc.setTextColor(...r.color);
    else doc.setTextColor(...COLOR_DARK);
    doc.setFont("helvetica", r.bold ? "bold" : "normal");
    doc.setFontSize(r.bold ? 10.5 : 9);
    doc.text(r.label, boxX + 4, rowY);
    doc.text(r.value, boxX + boxW - 4, rowY, { align: "right" });
    rowY += lineH;
  });
  doc.setTextColor(...COLOR_DARK);

  // Mention HT
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(
    "Exonération de TVA – Article 262 du CGI (Exportation). Tous les montants sont en euros hors taxes (HT).",
    14,
    tY + boxH + 6,
  );
  doc.setTextColor(...COLOR_DARK);

  // ===== Payments history =====
  if (paiements && paiements.length > 0) {
    autoTable(doc, {
      startY: tY + boxH + 14,
      head: [["Date", "Méthode", "Détail", "Montant"]],
      body: paiements.map((p) => [fmtDate(p.date_paiement), p.methode, p.notes || "—", fmt(p.montant)]),
      headStyles: { fillColor: COLOR_DARK, textColor: 255, fontStyle: "bold" },
      columnStyles: { 3: { halign: "right", cellWidth: 35 } },
      styles: { fontSize: 9, cellPadding: 3 },
      theme: "grid",
      margin: { left: 14, right: 14 },
    });
  }

  // ===== Footer =====
  doc.setDrawColor(...COLOR_KLEIN);
  doc.setLineWidth(0.6);
  doc.line(14, pageHeight - 18, pageWidth - 14, pageHeight - 18);
  doc.setLineWidth(0.2);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(100);
  const footerParts = [companyName, s.telephone, s.email, s.adresse, s.rib && `RIB : ${s.rib}`].filter(Boolean);
  doc.text(footerParts.join("  ·  "), pageWidth / 2, pageHeight - 12, { align: "center" });
  if (s.site_web) {
    doc.text(s.site_web, pageWidth / 2, pageHeight - 7, { align: "center" });
  }
  doc.setTextColor(...COLOR_DARK);

  doc.save(`recu-${vente.plaque || vente.id}.pdf`);
}

function fmtInterStatut(s) {
  return { a_faire: "À FAIRE", en_cours: "EN COURS", termine: "TERMINÉ" }[s] || s;
}

export function generateWorkOrderPDF(vente, interventions) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const today = new Date().toLocaleDateString("fr-FR");

  // Header band
  doc.setFillColor(10, 10, 10);
  doc.rect(0, 0, pageWidth, 30, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("GESTION CONCESSION AUTOMOBILE", 14, 19);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("FICHE ATELIER · INTERVENTIONS MÉCANIQUES", 14, 25);

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("ORDRE DE TRAVAIL", pageWidth - 14, 19, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(`Édité le ${today}`, pageWidth - 14, 25, { align: "right" });

  doc.setTextColor(20, 20, 20);

  // Vehicle box
  const yBox = 40;
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.5);
  doc.rect(14, yBox, pageWidth - 28, 36);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("VÉHICULE", 18, yBox + 7);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(`${vente.marque || ""} ${vente.modele || ""} ${vente.annee ? `(${vente.annee})` : ""}`, 18, yBox + 16);
  doc.setFontSize(9);
  doc.text(`VIN : ${vente.vin || "—"}`, 18, yBox + 23);
  doc.text(`Couleur : ${vente.couleur || "—"}`, 18, yBox + 29);
  doc.text(`Kilométrage : ${vente.kilometrage || 0} km`, 80, yBox + 29);

  // Plate
  doc.setDrawColor(0);
  doc.setLineWidth(0.8);
  doc.rect(pageWidth - 70, yBox + 6, 56, 16);
  doc.setFont("courier", "bold");
  doc.setFontSize(16);
  doc.text(vente.plaque || "—", pageWidth - 42, yBox + 17, { align: "center" });
  doc.setLineWidth(0.2);

  // Client + dates
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("CLIENT", pageWidth - 70, yBox + 28);
  doc.setFont("helvetica", "normal");
  doc.text(vente.client_nom || "—", pageWidth - 50, yBox + 28);

  // ---- Travaux À FAIRE / EN COURS ----
  const aFaire = interventions.filter((i) => i.statut !== "termine");
  let cursorY = yBox + 44;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setFillColor(230, 57, 70);
  doc.setTextColor(255, 255, 255);
  doc.rect(14, cursorY, pageWidth - 28, 7, "F");
  doc.text(`TRAVAUX À RÉALISER  (${aFaire.length})`, 18, cursorY + 5);
  doc.setTextColor(20, 20, 20);
  cursorY += 9;

  if (aFaire.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.text("Aucune intervention à réaliser.", 18, cursorY + 4);
    cursorY += 10;
  } else {
    autoTable(doc, {
      startY: cursorY,
      head: [["#", "Type", "Description", "Mécanicien", "Pièces", "M.O.", "Statut", "Fait"]],
      body: aFaire.map((it, idx) => [
        idx + 1,
        it.type || "—",
        it.description || "",
        it.mecanicien || "—",
        `${Number(it.pieces || 0).toFixed(2)} €`,
        `${Number(it.main_oeuvre || 0).toFixed(2)} €`,
        fmtInterStatut(it.statut),
        "☐",
      ]),
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: "bold", fontSize: 9 },
      columnStyles: {
        0: { halign: "center", cellWidth: 8 },
        1: { cellWidth: 22 },
        2: { cellWidth: "auto" },
        4: { halign: "right", cellWidth: 18 },
        5: { halign: "right", cellWidth: 18 },
        6: { halign: "center", cellWidth: 20 },
        7: { halign: "center", cellWidth: 10 },
      },
      styles: { fontSize: 9, cellPadding: 2 },
      theme: "grid",
    });
    cursorY = doc.lastAutoTable.finalY + 6;
  }

  // ---- Travaux TERMINÉS (récap) ----
  const done = interventions.filter((i) => i.statut === "termine");
  if (done.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setFillColor(16, 185, 129);
    doc.setTextColor(255, 255, 255);
    doc.rect(14, cursorY, pageWidth - 28, 7, "F");
    doc.text(`TRAVAUX DÉJÀ EFFECTUÉS  (${done.length})`, 18, cursorY + 5);
    doc.setTextColor(20, 20, 20);
    cursorY += 9;

    autoTable(doc, {
      startY: cursorY,
      head: [["Date", "Type", "Description", "Mécanicien", "Coût"]],
      body: done.map((it) => [
        it.date ? new Date(it.date).toLocaleDateString("fr-FR") : "—",
        it.type || "—",
        it.description || "",
        it.mecanicien || "—",
        `${Number(it.cout_total || 0).toFixed(2)} €`,
      ]),
      headStyles: { fillColor: [100, 116, 139], textColor: 255, fontStyle: "bold", fontSize: 9 },
      columnStyles: {
        4: { halign: "right", cellWidth: 22 },
      },
      styles: { fontSize: 9, cellPadding: 2 },
      theme: "striped",
    });
    cursorY = doc.lastAutoTable.finalY + 6;
  }

  // Totals
  const totalEstime = aFaire.reduce(
    (s, it) => s + Number(it.pieces || 0) + Number(it.main_oeuvre || 0),
    0
  );
  const totalDone = done.reduce((s, it) => s + Number(it.cout_total || 0), 0);

  doc.setDrawColor(220);
  doc.line(pageWidth - 100, cursorY + 2, pageWidth - 14, cursorY + 2);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Estimation travaux à réaliser", pageWidth - 100, cursorY + 8);
  doc.text(`${totalEstime.toFixed(2)} €`, pageWidth - 14, cursorY + 8, { align: "right" });
  doc.text("Total déjà effectué", pageWidth - 100, cursorY + 14);
  doc.text(`${totalDone.toFixed(2)} €`, pageWidth - 14, cursorY + 14, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Total mécanique", pageWidth - 100, cursorY + 22);
  doc.text(`${(totalEstime + totalDone).toFixed(2)} €`, pageWidth - 14, cursorY + 22, { align: "right" });

  // Signatures
  const sigY = cursorY + 38;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setDrawColor(160);
  doc.line(18, sigY, 90, sigY);
  doc.text("Signature mécanicien", 18, sigY + 5);
  doc.line(pageWidth - 90, sigY, pageWidth - 14, sigY);
  doc.text("Signature contrôle qualité", pageWidth - 90, sigY + 5);

  // Footer
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(
    "Document généré par Gestion Concession Automobile · Cochez la case « Fait » au fur et à mesure des interventions.",
    14,
    doc.internal.pageSize.getHeight() - 10
  );

  doc.save(`fiche-atelier-${vente.plaque || vente.id}.pdf`);
}
